import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchiveEntry() {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    setEntry(null);
    setNotFound(false);
    axios
      .get(`${API}/entries/${slug}`)
      .then((r) => setEntry(r.data))
      .catch(() => setNotFound(true));
    axios
      .get(`${API}/entries`)
      .then((r) => {
        const idx = r.data.findIndex((e) => e.slug === slug);
        setNeighbors({
          prev: idx > 0 ? r.data[idx - 1] : null,
          next: idx >= 0 && idx < r.data.length - 1 ? r.data[idx + 1] : null,
        });
      })
      .catch(() => {});
  }, [slug]);

  return (
    <main className="relative bg-[#05070F] text-[#F8F8F8] min-h-screen" data-testid="archive-entry-page">
      <div className="aura-field" aria-hidden="true" />
      <header className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 sm:px-12 py-5 backdrop-blur-md bg-[#080D1C]/70 border-b border-blue-100/10">
        <Link to="/" data-testid="entry-back-home" className="font-display font-extrabold text-sm tracking-tight">
          JW.
        </Link>
        <Link
          to="/"
          onClick={() => setTimeout(() => window.__lenis?.scrollTo("#archive", { immediate: true }), 60)}
          data-testid="entry-back-archive"
          className="font-meta text-[10px] sm:text-xs tracking-[0.25em] text-neutral-400 hover:text-white transition-colors duration-300"
        >
          ← THE ARCHIVE
        </Link>
      </header>

      {notFound && (
        <div className="min-h-screen flex items-center justify-center" data-testid="entry-not-found">
          <p className="font-meta text-xs tracking-[0.3em] text-neutral-500">ENTRY NOT FOUND.</p>
        </div>
      )}

      {entry && (
        <article className="max-w-3xl mx-auto px-6 pt-36 sm:pt-44 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-meta text-[10px] sm:text-xs tracking-[0.25em] text-neutral-500" data-testid="entry-meta">
              <span>{entry.date_display}</span>
              <span className="w-4 h-px bg-white/30" />
              <span>{entry.type}</span>
              {entry.themes.map((t) => (
                <span key={t} className="border border-white/15 px-2.5 py-1">{t}</span>
              ))}
            </div>

            <h1
              className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-[clamp(2.6rem,8vw,5.5rem)] mt-8"
              data-testid="entry-title"
            >
              {entry.title}
            </h1>

            {entry.pull_quote && (
              <blockquote className="border-l-2 border-white pl-6 my-12 sm:my-16" data-testid="entry-pull-quote">
                <p className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight leading-snug text-neutral-200">
                  “{entry.pull_quote}”
                </p>
              </blockquote>
            )}

            <div
              className="whitespace-pre-wrap text-lg sm:text-xl font-light leading-[2] text-neutral-200"
              data-testid="entry-text"
            >
              {entry.text}
            </div>
          </motion.div>

          <nav className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mt-24">
            {neighbors.prev ? (
              <Link
                to={`/archive/${neighbors.prev.slug}`}
                data-testid="entry-prev-link"
                className="bg-[#0A0A0A] p-6 sm:p-8 hover:bg-white hover:text-black transition-colors duration-300"
              >
                <span className="font-meta text-[10px] tracking-[0.25em] text-neutral-500 block mb-3">← EARLIER</span>
                <span className="font-display text-base sm:text-lg font-bold uppercase tracking-tight">{neighbors.prev.title}</span>
              </Link>
            ) : (
              <div className="bg-[#0A0A0A] p-6 sm:p-8" />
            )}
            {neighbors.next ? (
              <Link
                to={`/archive/${neighbors.next.slug}`}
                data-testid="entry-next-link"
                className="bg-[#0A0A0A] p-6 sm:p-8 hover:bg-white hover:text-black transition-colors duration-300 text-right"
              >
                <span className="font-meta text-[10px] tracking-[0.25em] text-neutral-500 block mb-3">LATER →</span>
                <span className="font-display text-base sm:text-lg font-bold uppercase tracking-tight">{neighbors.next.title}</span>
              </Link>
            ) : (
              <div className="bg-[#0A0A0A] p-6 sm:p-8">
                <span className="font-meta text-[10px] tracking-[0.25em] text-neutral-600 block text-right">MORE IS BEING WRITTEN.</span>
              </div>
            )}
          </nav>
        </article>
      )}
    </main>
  );
}
